<template>
    <div class="faq-page">
        <div v-if="isLoading" class="loading-overlay"><p>Carregando...</p></div>

        <div v-if="msgAlerta" class="info-alert row-space" @click="msgAlerta = null">
            {{ msgAlerta }}
        </div>


        <div class="page-header">
            <div>
                <h1>Perguntas Frequentes</h1>
                <p>Consulte orientações rápidas, dúvidas comuns e instruções de uso do sistema.</p>
            </div>

            <div class="header-actions">
                <div class="header-badge">
                    <span>{{ faqsFiltradas.length }}</span>
                    <small>FAQ(s)</small>
                </div>

                <button type="button" class="btn-back" @click="closeForm">
                    ← Voltar
                </button>
            </div>
        </div>
        

        <div class="faq-toolbar">
            <div class="toolbar-field search-field">
                <label>Pesquisar</label>
                <input
                    v-model="filtros.termo"
                    type="text"
                    placeholder="Digite uma palavra-chave, pergunta ou assunto..."
                />
            </div>

            <div class="toolbar-field">
                <label>Grupo</label>
                <select v-model="filtros.grupo">
                    <option value="">Todos</option>
                    <option v-for="grupo in grupos" :key="grupo" :value="grupo">
                        {{ grupo }}
                    </option>
                </select>
            </div>
        </div>

        <div v-if="!isLoading && gruposFaq.length === 0" class="empty-state">
            <h3>Nenhum FAQ encontrado</h3>
            <p>Revise os filtros informados ou tente pesquisar por outro termo.</p>
        </div>

        <div
            v-for="grupo in gruposFaq"
            :key="grupo.nome"
            class="faq-group"
        >
            <div class="faq-group-header" @click="alternarGrupo(grupo.nome)">
                <div>
                    <h2>{{ grupo.nome }}</h2>
                    <span>{{ grupo.itens.length }} pergunta(s)</span>
                </div>

                <button type="button" class="btn-toggle-group">
                    {{ grupoEstaAberto(grupo.nome) ? 'Recolher ▲' : 'Expandir ▼' }}
                </button>
            </div>

            <div v-show="grupoEstaAberto(grupo.nome)" class="faq-list">
                <div
                    v-for="item in grupo.itens"
                    :key="item.uid"
                    class="faq-item"
                    :class="{ aberto: itemEstaAberto(item.uid) }"
                >
                    <button class="faq-question" @click="alternarItem(item.uid)">
                        <div class="faq-question-left">
                            <h3>{{ item.nmtitulo }}</h3>

                            <div v-if="palavrasChave(item).length" class="faq-tags">
                                <span
                                    v-for="palavra in palavrasChave(item)"
                                    :key="palavra"
                                    class="tag"
                                >
                                    {{ palavra }}
                                </span>
                            </div>
                        </div>

                        <span class="faq-icon">
                            {{ itemEstaAberto(item.uid) ? '−' : '+' }}
                        </span>
                    </button>

                    <div v-if="itemEstaAberto(item.uid)" class="faq-answer">
                        <div
                            class="faq-html"
                            v-html="item.descricao || 'Descrição não informada.'"
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script setup>
import { computed, reactive, ref, onMounted } from 'vue';
import { Api } from '@/funcoes/apiExport';

const emit = defineEmits(['close']);

const isLoading = ref(false)
const lsDados = ref([])
const msgAlerta = ref(null)

const itensAbertos = ref({})
const gruposRecolhidos = ref({})

function closeForm() {
    emit('close');
}

const filtros = reactive({
    termo: '',
    grupo: ''
})

const grupos = computed(() => {
    const lista = lsDados.value.map(item => item.nmgrupo || 'OUTROS')
    return [...new Set(lista)].sort((a, b) => a.localeCompare(b))
})

const faqsFiltradas = computed(() => {
    const termo = filtros.termo.trim().toLowerCase()

    return lsDados.value.filter(item => {
        const grupo = item.nmgrupo || 'OUTROS'

        const atendeGrupo = !filtros.grupo || grupo === filtros.grupo

        const textoBusca = [
            item.nmgrupo,
            item.nmtitulo,
            item.descricao,
            item.palavras_chave
        ]
            .join(' ')
            .toLowerCase()

        const atendeTermo = !termo || textoBusca.includes(termo)

        return atendeGrupo && atendeTermo
    })
})

const gruposFaq = computed(() => {
    const map = {}

    faqsFiltradas.value.forEach((item, index) => {
        const grupo = item.nmgrupo || 'OUTROS'

        if (!map[grupo]) {
            map[grupo] = {
                nome: grupo,
                itens: []
            }
        }

        map[grupo].itens.push({
            ...item,
            uid: `${grupo}-${item.nmtitulo}-${index}`
        })
    })

    return Object.values(map)
})

function grupoEstaAberto(nome) {
    return gruposRecolhidos.value[nome] !== true
}

function alternarGrupo(nome) {
    gruposRecolhidos.value[nome] = !gruposRecolhidos.value[nome]
}

function itemEstaAberto(uid) {
    return itensAbertos.value[uid] === true
}

function alternarItem(uid) {
    itensAbertos.value[uid] = !itensAbertos.value[uid]
}

function palavrasChave(item) {
    return String(item.palavras_chave || '')
        .split(';')
        .map(p => p.trim())
        .filter(Boolean)
}

async function lerDados() {
    try {
        msgAlerta.value = null
        isLoading.value = true

        const req = await Api.genericPost('/listas/sys/help', { intparam1: 4 })
        lsDados.value = req ?? []

        gruposRecolhidos.value = {}
        itensAbertos.value = {}

        lsDados.value.forEach(item => {
            const grupo = item.nmgrupo || 'OUTROS'
            gruposRecolhidos.value[grupo] = true
        })
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: Não foi possível acessar dados do servidor: "${error.message}"`
    } finally {
        isLoading.value = false
    }
}

onMounted(lerDados)
</script>


<style scoped>
.btn-back {
    width: auto;
    min-width: 90px;
    height: 34px;
    padding: 0 14px;
    border: 1px solid #cbd5e1;
    border-radius: 999px;
    background: #ffffff;
    color: #334155;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
}

.btn-back:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
    color: #1f2937;
}

.faq-page {
    width: 100%;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    text-align: left;
    box-sizing: border-box;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 14px;
    background: #ffffff;
    border: 1px solid #d9e2ec;
    border-radius: 12px;
    padding: 16px 18px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.page-header h1 {
    margin: 0;
    font-size: 22px;
    color: #1f2937;
}

.page-header p {
    margin: 4px 0 0;
    font-size: 13px;
    color: #64748b;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

.header-badge {
    min-width: 85px;
    background: #eff6f5;
    border: 1px solid #b7d0cc;
    border-radius: 10px;
    padding: 8px 10px;
    text-align: center;
    color: #25413e;
}

.header-badge span {
    display: block;
    font-size: 22px;
    font-weight: 800;
    line-height: 1;
}

.header-badge small {
    display: block;
    margin-top: 3px;
    font-size: 11px;
}

.faq-toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 1fr) 260px;
    gap: 10px;
    background: #ffffff;
    border: 1px solid #d9e2ec;
    border-radius: 12px;
    padding: 14px;
}

.toolbar-field {
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.toolbar-field label {
    font-weight: bold;
    text-align: left;
    font-size: 0.9em;
    opacity: .75;
}

.faq-group {
    background: #ffffff;
    border: 1px solid #d9e2ec;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}

.faq-group-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 14px 16px;
    cursor: pointer;
    background: #f8fafc;
    border-bottom: 1px solid #e5e7eb;
}

.faq-group-header h2 {
    margin: 0;
    font-size: 16px;
    color: #1f2937;
}

.faq-group-header span {
    display: block;
    margin-top: 3px;
    font-size: 12px;
    color: #64748b;
}

.btn-toggle-group {
    width: auto;
    min-width: 105px;
    border: none;
    background: #e2e8f0;
    color: #334155;
    border-radius: 999px;
    padding: 7px 12px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}

.btn-toggle-group:hover {
    background: #cbd5e1;
    color: #1f2937;
}

.faq-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
}

.faq-item {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
}

.faq-item.aberto {
    border-color: #94a3b8;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.faq-question {
    width: 100%;
    padding: 12px 14px;
    border: none;
    border-radius: 0;
    background: #ffffff;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    text-align: left;
    cursor: pointer;
}

.faq-question:hover {
    background: #f8fafc;
    color: inherit;
}

.faq-question-left {
    flex: 1;
    min-width: 0;
}

.faq-question h3 {
    margin: 0;
    font-size: 15px;
    color: #1f2937;
    line-height: 1.3;
}

.faq-tags {
    margin-top: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.tag {
    background: #eef4ff;
    color: #1d4e89;
    border-radius: 999px;
    padding: 3px 8px;
    font-size: 11px;
    font-weight: 700;
}

.faq-icon {
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #e2e8f0;
    color: #334155;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    line-height: 1;
    font-weight: 700;
}

.faq-answer {
    padding: 12px 14px;
    border-top: 1px solid #edf2f7;
    background: #f8fafc;
}

.faq-html {
    color: #425466;
    line-height: 1.55;
    font-size: 14px;
}

.faq-html :deep(p) {
    margin: 0 0 10px;
}

.faq-html :deep(p:last-child) {
    margin-bottom: 0;
}

.faq-html :deep(ul),
.faq-html :deep(ol) {
    margin: 8px 0;
    padding-left: 22px;
}

.faq-html :deep(li) {
    margin-bottom: 4px;
}

.faq-html :deep(strong) {
    color: #243447;
}

.empty-state {
    border: 1px dashed #cbd5e1;
    background: #f8fafc;
    border-radius: 10px;
    padding: 22px;
    color: #64748b;
    text-align: center;
    font-size: 13px;
}

.empty-state h3 {
    margin: 0 0 6px;
    color: #1f2937;
}

.empty-state p {
    margin: 0;
}

@media (max-width: 800px) {
    .page-header {
        flex-direction: column;
    }

    .header-actions {
        width: 100%;
        justify-content: space-between;
    }

    .faq-toolbar {
        grid-template-columns: 1fr;
    }

    .faq-group-header {
        align-items: flex-start;
        flex-direction: column;
    }

    .btn-toggle-group {
        width: 100%;
    }
}
</style>
